import { html, css, LitElement } from 'lit';
import { property } from 'lit/decorators.js';
import './seb-textfield.js';
import includesSearch from './utils/includesSearch.js';

export class SebAutocomplete extends LitElement {
  static styles = css`
    :host {
      display: block;
      color: var(--seb-text-color, #111927);
    }

    :host div {
      position: relative;
    }

    :host div datalist {
      display: block;
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      opacity: 0;
      visibility: hidden;
      -webkit-text-size-adjust: 100%;
      -webkit-font-smoothing: antialiased;
      font-size: 1rem;
      font-weight: 400;
      line-height: 1.5;
      font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji";
      box-sizing: border-box;
      list-style: none;
      margin: 0;
      padding: 8px 0;
      background-color: #fff;
      color: #111927;
      -webkit-transition: box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
      transition: box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
      border-radius: 8px;
      box-shadow: 0 6px 30px rgba(0, 0, 0, 0.08);
      background-image: none;
      -webkit-overflow-scrolling: touch;
      overflow-y: auto;
      overflow-x: hidden;
      min-width: 16px;
      min-height: 38px;
      max-height: 600px;
      outline: 0;
      z-index: 10000;
    }

    :host div datalist.show {
      opacity: 1;
      visibility: visible;
    }

    :host div datalist option {
      cursor: pointer;
      width: 100%;
      -webkit-text-size-adjust: 100%;
      -webkit-font-smoothing: antialiased;
      list-style: none;
      -webkit-tap-highlight-color: transparent;
      outline: 0;
      border: 0;
      margin: 0;
      border-radius: 0;
      user-select: none;
      vertical-align: middle;
      -webkit-appearance: none;
      color: inherit;
      font-size: 1rem;
      font-weight: 400;
      line-height: 1.5;
      font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji";
      display: flex;
      -webkit-box-pack: start;
      justify-content: flex-start;
      -webkit-box-align: center;
      align-items: center;
      position: relative;
      text-decoration: none;
      box-sizing: border-box;
      white-space: nowrap;
      padding: 6px 16px;
      min-height: auto;
      background-color: transparent;
    }

    :host div datalist option:hover {
      background-color: rgba(99, 102, 241, 0.08);
    }

    :host div svg.arrow,
    :host div svg.close {
      position: absolute;
      top: -12px;
      right: 16px;
      -webkit-text-size-adjust: 100%;
      -webkit-font-smoothing: antialiased;
      color-scheme: light;
      --Grid-columns: 12;
      --Grid-columnSpacing: 32px;
      --Grid-rowSpacing: 32px;
      -webkit-tap-highlight-color: transparent;
      cursor: pointer;
      text-align: center;
      color: #6C737F;
      box-sizing: border-box;
      user-select: none;
      width: 1em;
      height: 1em;
      display: inline-block;
      fill: currentColor;
      flex-shrink: 0;
      transition: fill 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
      font-size: 1.5rem;
    }

    :host div svg.close {
      right: 48px;
      opacity: 0;
      visibility: hidden;
    }

    :host div:hover svg.close {
      right: 48px;
      opacity: 1;
      visibility: visible;
    }

    :host div svg.arrow.rotate {
      transform: rotate(180deg);
    }
  `;

  @property({ type: Boolean }) checked = false;

  @property({ type: String }) name = '';

  @property({ type: String }) value = '';

  @property({ type: String }) label = 'Label';

  @property({ type: String }) noResultsText = 'No results';

  @property({ type: Array }) options = [];

  constructor() {
    super();
    this.addEventListener('blur', this.__handleBlur);
  }

  __handleFocus(event: Event) {
    const isOption = event.target instanceof HTMLOptionElement;
    if (!isOption) {
      this.shadowRoot?.querySelector('datalist')?.classList.add('show');
      this.shadowRoot?.querySelector('svg.arrow')?.classList.add('rotate');
    }
  }

  __handleBlur() {
    this.shadowRoot?.querySelector('datalist')?.classList.remove('show');
    this.shadowRoot?.querySelector('svg.arrow')?.classList.remove('rotate');
  }

  __handleOptionClick(event: Event) {
    const option = event.target as HTMLOptionElement;
    this.value = option.text;
    this.__handleBlur();
    this.dispatchEvent(new CustomEvent('input', {
      detail: option.value || option.text,
    }));
  }

  __filteredOptions() {
    const parsedOptions: {text: string; value: string}[] = JSON.parse(
      JSON.stringify(this.options || [])
    );
    if (!this.value) return parsedOptions;
    return parsedOptions.filter(option =>
      includesSearch(option.text, this.value)
    );
  }

  __handleInput(event: Event) {
    const input = event.target as HTMLInputElement;
    this.value = input.value;
  }

  _handleClear() {
    this.value = '';
    this.dispatchEvent(new CustomEvent('input', {
      detail: '',
    }));
  }

  render() {
    const options = this.__filteredOptions() || [];
    return html`
      <div>
        <datalist tabindex='0'>
          ${options.length === 0
            ? html`<option value=''>${this.noResultsText}</option>`
            : options.map(option =>
            html`
              <option
                value=${option.value}
                @click=${this.__handleOptionClick}
              >
                ${option.text}
              </option>
            `
          )}
        </datalist>

        <seb-textfield
          .name=${this.name}
          .value=${this.value}
          .label=${this.label}
          @input=${this.__handleInput}
          @focus=${this.__handleFocus}
        />

        ${this?.value?.length > 0 ? html`
          <svg
            role='button'
            class='close'
            width="24"
            height="24"
            focusable="false"
            aria-hidden="true"
            viewBox="0 0 24 24"
            data-testid="CloseDropDownIcon"
            @click=${this._handleClear}
          >
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"></path>
          </svg>
        ` : ``}

        <svg
          class='arrow'
          width="24"
          height="24"
          focusable="false"
          aria-hidden="true"
          viewBox="0 0 24 24"
          data-testid="ArrowDropDownIcon"
        >
          <path d="M7 10l5 5 5-5z"></path>
        </svg>
      </div>
    `;
  }
}
